/**
 * U2.4. The taste questions as pure functions: what the form holds, what it
 * becomes, and what a saved vector reads back as.
 *
 * ## Why this file mirrors `src/taste/starter.ts` instead of importing it
 *
 * `starterVector` ends in `TasteVector.parse`, and `src/taste/vector.ts`
 * imports zod, which is the Worker's dependency and not the app's. The app
 * cannot import either, so the arithmetic is written out here, in a file that
 * depends on nothing, and `test/taste-form.test.ts` holds the two together:
 * the same answers must give the same vector on the three fixtures in
 * `test/fixtures/taste/`, and the eight lanes and four weights must be the
 * ones `vector.ts` and `starter.ts` export. A change to one that forgets the
 * other fails a test rather than shipping a vector the ranker reads wrongly.
 *
 * ## Two values that were nearly one
 *
 * `englishOnly` is not asked here. It is `profiles.english_only`, answered on
 * the profile step and edited under "You"; the taste step reads it and says it
 * again in `free_text` (as `starterVector` does) and never owns it. A second
 * switch would be two answers to one question the day they disagreed.
 *
 * `free_text` holds two things — that sentence and the person's own words —
 * and shares one 600-character budget. `freeTextMax` is how much of it the
 * person's words may have, so the sentence is never what gets cut.
 */

export const VECTOR_VERSION = '2.0'

/** `Lane` of `src/taste/vector.ts`, mirrored (its module needs zod). */
export const LANES = [
  'classical', 'film', 'clubs', 'gigs', 'talks', 'exhibitions', 'theatre', 'city',
] as const
export type Lane = typeof LANES[number]

/** 0 never, 1 sometimes, 2 often, 3 always. */
export type Level = 0 | 1 | 2 | 3

/** `Answers` of `src/taste/starter.ts`, mirrored. */
export interface Answers {
  lanes: Partial<Record<Lane, Level>>
  anchors?: string[]
  classical?: { excludeChamber?: boolean; excludeFilmMusic?: boolean; orchestralOnly?: boolean }
  englishOnly: boolean
}

/** The shape `TasteVector` validates, as a plain type. */
export interface VectorJson {
  version: '2.0'
  lanes: Record<Lane, number>
  classical?: { exclude_chamber: boolean; exclude_film_music: boolean; orchestral_only: boolean }
  anchors: { artists: string[]; composers: string[]; venues: string[]; festivals: string[] }
  free_text?: string
}

/** Lane weights for the four points. `starter.ts`'s `LEVEL_WEIGHT`, mirrored. */
export const LEVEL_WEIGHT: Record<Level, number> = { 0: 0, 1: 0.35, 2: 0.65, 3: 0.9 }
export const MAX_ANCHORS = 40
const MAX_ANCHOR_LEN = 80
const MIN_ANCHOR_LEN = 3
export const FREE_TEXT_MAX = 600
/** The sentence `starterVector` adds for someone who reads English only. */
export const ENGLISH_ONLY_TEXT = 'Reads and hears English only: events that need German are not for this person.'

// ---------------------------------------------------------------------------
// The rows the form draws
// ---------------------------------------------------------------------------

export const LANE_ROWS: readonly { lane: Lane; label: string }[] = [
  { lane: 'classical', label: 'Classical' },
  { lane: 'film', label: 'Film' },
  { lane: 'clubs', label: 'Clubs & DJs' },
  { lane: 'gigs', label: 'Gigs' },
  { lane: 'talks', label: 'Talks' },
  { lane: 'exhibitions', label: 'Exhibitions' },
  { lane: 'theatre', label: 'Theatre & dance' },
  { lane: 'city', label: 'City events' },
]

/** The four-step control's options. `Segmented` speaks in strings. */
export const LEVEL_OPTIONS: readonly { value: string; label: string }[] = [
  { value: '0', label: 'Never' },
  { value: '1', label: 'Sometimes' },
  { value: '2', label: 'Often' },
  { value: '3', label: 'Always' },
]

export const levelOf = (value: string): Level => {
  const n = Number(value)
  return n === 0 || n === 1 || n === 2 || n === 3 ? n : 1
}

// ---------------------------------------------------------------------------
// The draft
// ---------------------------------------------------------------------------

/** What the form holds while it is open. Nothing derived: every field is the
 *  thing a control shows. The three classical switches are phrased as the
 *  person reads them ("Chamber music too"), and inverted on the way out. */
export interface TasteDraft {
  lanes: Record<Lane, Level>
  chamberToo: boolean
  filmMusicToo: boolean
  onlyOrchestral: boolean
  /** The comma-separated names, as typed. */
  names: string
  more: string
}

/** A lane the person has not touched is "Sometimes" — what `starterVector`
 *  does with a lane it was not told about, so a blank form is the blank
 *  answers, not a different default. */
export const DEFAULT_LEVEL: Level = 1

export function blankDraft(): TasteDraft {
  const lanes = {} as Record<Lane, Level>
  for (const l of LANES) lanes[l] = DEFAULT_LEVEL
  return { lanes, chamberToo: true, filmMusicToo: true, onlyOrchestral: false, names: '', more: '' }
}

/** The classical switches are asked only of someone who goes to classical at
 *  all; at Never they are neither shown nor sent. */
export const asksClassicalFlags = (d: Pick<TasteDraft, 'lanes'>): boolean => d.lanes.classical >= 1

/** How much of `free_text` the person's own words may take. */
export const freeTextMax = (englishOnly: boolean): number =>
  englishOnly ? FREE_TEXT_MAX - ENGLISH_ONLY_TEXT.length - 1 : FREE_TEXT_MAX

// ---------------------------------------------------------------------------
// Names
// ---------------------------------------------------------------------------

/** "Alder Quartet Hall, Gustavo Fenwick,, Berghain" as its pieces. Commas
 *  and line breaks both split — a person pasting a list on separate lines
 *  meant the same thing — and nothing else does, so "Sonic Youth" stays one
 *  name. */
export function splitNames(text: string): string[] {
  return text.split(/[,\n]/).map(s => s.trim().replace(/\s+/g, ' ')).filter(Boolean)
}

/** Lowercase with accents and combining marks stripped, as `rank.ts` folds
 *  an anchor before it looks for it in a row, so "Kunstquartier BETHANIEN"
 *  and "kunstquartier bethanien" are one venue. */
const fold = (s: string): string => s.normalize('NFD').replace(/\p{M}/gu, '').toLocaleLowerCase('en').replace(/\s+/g, ' ').trim()

/** What the match needs of a venue. `VenueRow` has more. */
export interface VenueName { name: string; aliases?: string[] | null }

/**
 * Splits clean names into artists and venues. A name that is, once folded,
 * a venue's name or one of its aliases goes to `venues` under the venue's own
 * spelling; everything else is an artist. Whole-name equality only: "Berghain
 * resident" is not Berghain, and a substring test would send half the feed's
 * titles to `venues`.
 */
export function placeNames(names: readonly string[], venues: readonly VenueName[]): { artists: string[]; venues: string[] } {
  const byName = new Map<string, string>()
  for (const v of venues) {
    for (const k of [v.name, ...(v.aliases ?? [])]) {
      const key = fold(k ?? '')
      if (key && !byName.has(key)) byName.set(key, v.name)
    }
  }
  const out = { artists: [] as string[], venues: [] as string[] }
  for (const n of names) {
    const venue = byName.get(fold(n))
    if (venue) { if (!out.venues.includes(venue)) out.venues.push(venue) } else out.artists.push(n)
  }
  return out
}

// ---------------------------------------------------------------------------
// Draft -> answers -> vector
// ---------------------------------------------------------------------------

/** The draft as `starterVector`'s answers. Anchors are every name, unplaced:
 *  `starterVector` stores them all as artists and `vectorFrom` then moves the
 *  venues. */
export function answersFrom(d: TasteDraft, englishOnly: boolean): Answers {
  const a: Answers = { lanes: { ...d.lanes }, anchors: splitNames(d.names), englishOnly }
  if (asksClassicalFlags(d)) {
    // "Only orchestral" says no to everything else the funnel knows, so the
    // two exclusions are on whatever the hidden switches last said.
    a.classical = {
      excludeChamber: d.onlyOrchestral || !d.chamberToo,
      excludeFilmMusic: d.onlyOrchestral || !d.filmMusicToo,
      orchestralOnly: d.onlyOrchestral,
    }
  }
  return a
}

/** `starterVector` without the validation, which needs zod. The arithmetic is
 *  the same and a test holds them equal. */
export function starterPure(a: Answers): VectorJson {
  const lanes = {} as Record<Lane, number>
  for (const lane of LANES) {
    const level = a.lanes[lane] ?? 1
    if (!(level in LEVEL_WEIGHT)) throw new Error(`starter: lane ${lane} has level ${String(level)}, expected 0-3`)
    lanes[lane] = LEVEL_WEIGHT[level]
  }
  const seen = new Set<string>()
  const artists: string[] = []
  for (const raw of a.anchors ?? []) {
    const name = raw.trim().replace(/\s+/g, ' ')
    const key = name.toLocaleLowerCase('de')
    if (name.length < MIN_ANCHOR_LEN || name.length > MAX_ANCHOR_LEN || seen.has(key)) continue
    seen.add(key)
    artists.push(name)
    if (artists.length >= MAX_ANCHORS) break
  }
  const out: VectorJson = {
    version: VECTOR_VERSION,
    lanes,
    anchors: { artists, composers: [], venues: [], festivals: [] },
  }
  if (a.classical) {
    out.classical = {
      exclude_chamber: !!a.classical.excludeChamber,
      exclude_film_music: !!a.classical.excludeFilmMusic,
      orchestral_only: !!a.classical.orchestralOnly,
    }
  }
  if (a.englishOnly) out.free_text = ENGLISH_ONLY_TEXT
  return out
}

/** Cut at a word, never past `max`. */
function cutAtWord(s: string, max: number): string {
  if (s.length <= max) return s
  const cut = s.slice(0, max)
  const sp = cut.lastIndexOf(' ')
  return (sp > 0 ? cut.slice(0, sp) : cut).replace(/[,;:\s]+$/, '')
}

/**
 * The vector the form saves: `starterPure` over the answers, the person's
 * venues moved out of `artists`, and their own words after the English-only
 * sentence in `free_text`. Their words are stored, not read — nothing on the
 * client interprets them (the model scorer does, per `profiles.model_scoring`).
 */
export function vectorFrom(d: TasteDraft, englishOnly: boolean, venues: readonly VenueName[] = []): VectorJson {
  const v = starterPure(answersFrom(d, englishOnly))
  const placed = placeNames(v.anchors.artists, venues)
  v.anchors.artists = placed.artists
  v.anchors.venues = placed.venues
  const more = cutAtWord(d.more.trim(), freeTextMax(englishOnly))
  const text = [englishOnly ? ENGLISH_ONLY_TEXT : '', more].filter(Boolean).join(' ')
  if (text) v.free_text = text
  else delete v.free_text
  return v
}

// ---------------------------------------------------------------------------
// A saved vector -> the draft (Account -> Your taste)
// ---------------------------------------------------------------------------

/** The point on the four-point scale whose weight is nearest. A starter
 *  vector holds exactly the four weights; this is only for a hand-edited one. */
export function levelFromWeight(w: unknown): Level {
  if (typeof w !== 'number' || !Number.isFinite(w)) return DEFAULT_LEVEL
  let best: Level = 0
  let bestD = Infinity
  for (const k of [0, 1, 2, 3] as const) {
    const d = Math.abs(LEVEL_WEIGHT[k] - w)
    if (d < bestD) { best = k; bestD = d }
  }
  return best
}

const isObj = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null && !Array.isArray(x)
const strs = (x: unknown): string[] => (Array.isArray(x) ? x.filter((s): s is string => typeof s === 'string') : [])

/**
 * Whether the form can edit what is saved. No active vector: yes, it makes
 * the first. A 2.0 vector — the form's own output: yes. Anything else is a
 * hand-built document the form cannot reproduce (Doug's is a 1.1), so Account
 * says nothing rather than offering to replace it, and the RPC refuses the
 * same case.
 */
export function canEditTaste(active: { version: string } | null | undefined): boolean {
  return !active || active.version === VECTOR_VERSION
}

/** A saved vector as the draft it came from. Defensive on every read: the
 *  column is `jsonb`, and a missing key is "not answered", not an error. */
export function draftFromVector(v: unknown): TasteDraft {
  const d = blankDraft()
  if (!isObj(v)) return d
  const lanes = isObj(v.lanes) ? v.lanes : {}
  for (const l of LANES) d.lanes[l] = levelFromWeight(lanes[l])
  if (isObj(v.classical)) {
    const c = v.classical
    d.onlyOrchestral = c.orchestral_only === true
    // With "only orchestral" on, the two exclusions were forced; reading them
    // back as "off" would make the switches lie once it is turned off.
    d.chamberToo = d.onlyOrchestral ? true : c.exclude_chamber !== true
    d.filmMusicToo = d.onlyOrchestral ? true : c.exclude_film_music !== true
  }
  const a = isObj(v.anchors) ? v.anchors : {}
  d.names = [...strs(a.artists), ...strs(a.venues)].join(', ')
  if (typeof v.free_text === 'string') {
    d.more = v.free_text.startsWith(ENGLISH_ONLY_TEXT) ? v.free_text.slice(ENGLISH_ONLY_TEXT.length).trim() : v.free_text
  }
  return d
}

/** Has the person changed anything since `base`? Compared on the vector, so a
 *  trailing comma or a retyped name is not a change. */
export function isTasteChanged(d: TasteDraft, base: TasteDraft, englishOnly: boolean): boolean {
  return JSON.stringify(vectorFrom(d, englishOnly)) !== JSON.stringify(vectorFrom(base, englishOnly))
}
